import * as colors from '../components/kp-main-menu/browser-colors.json';
import {range} from 'shared/utils/array-utils';
import {exists} from 'shared/utils/custom-utils';

type RGB = {
    r: number;
    g: number;
    b: number;
};

export function parseCssColorToRgb(color: string): RGB | null {
    color = color.toLowerCase().trim();

    // Check for hex color
    if (color.startsWith('#')) {
        return hexToRgb(color);
    }

    // Check for browser-supported color
    if (color in colors) {
        return hexToRgb(colors[color]);
    }

    // Check for rgb() and rgba()
    const rgbMatch = color.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*\d*\.?\d+)?\)$/);
    if (rgbMatch) {
        return {
            r: parseInt(rgbMatch[1], 10),
            g: parseInt(rgbMatch[2], 10),
            b: parseInt(rgbMatch[3], 10)
        };
    }

    // Check for hsl() and hsla()
    const hslMatch = color.match(/^hsla?\((\d+),\s*(\d+)%?,\s*(\d+)%?(?:,\s*\d*\.?\d+)?\)$/);
    if (hslMatch) {
        return hslToRgb(
            parseInt(hslMatch[1], 10),
            parseInt(hslMatch[2], 10),
            parseInt(hslMatch[3], 10)
        );
    }

    // If nothing passes, return null
    return null;
}

export function calculateRgbLuminance(rgb: RGB): number {
    // Standard color luminance from RGB calculation values
    return 0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b;
}

// Color convert calculations
function hexToRgb(hex: string): RGB | null {
    hex = hex.replace(/^#/, '');

    if (hex.length === 3) {
        hex = hex.split('').map((c) => c + c).join('');
    }

    if (hex.length === 6 || hex.length === 8) {
        const bigint = parseInt(hex, 16);
        const r = Math.floor(bigint / (256 * 256)) % 256;
        const g = Math.floor(bigint / 256) % 256;
        const b = bigint % 256;
        return {r, g, b};
    }

    return null;
}

function hslToRgb(h: number, s: number, l: number): RGB {
    s /= 100;
    l /= 100;
    const k = (n: number) => (n + h / 30) % 12;
    const a = s * Math.min(l, 1 - l);
    const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    return {r: Math.round(f(0) * 255), g: Math.round(f(8) * 255), b: Math.round(f(4) * 255)};
}

export function getColorFromString(str: string, lightness: number, opacity?: number): string {
    if (!str) {
        return `hsl(0, 0%, ${lightness}%)`;
    }

    const hues = range(0, 360, 10);
    const goldenFrac = 0.5 * (3 - Math.sqrt(5));
    const x = (simpleHash(str) * goldenFrac % 1.0) * (hues.length - 1);
    const i = Math.floor(x);
    const f = x % 1.0;
    const hue = (1.0 - f) * hues[i] + f * hues[i + 1];

    if (exists(opacity)) {
        return `hsla(${Math.round(hue * 100) / 100}, 100%, ${lightness}%, ${opacity})`;
    }

    return `hsl(${Math.round(hue * 100) / 100}, 100%, ${lightness}%)`;
}

export function getFondColor(fondId: number, lightness: number): string {
    return getColorFromString(fondId.toString(10), lightness);
}

function simpleHash(str: string): number {
    const prime = 31;
    let hash = 7;

    for (let i = 0; i < str.length; i++) {
        hash = (hash * prime + str.charCodeAt(i)) % Number.MAX_SAFE_INTEGER;
    }

    return hash;
}